import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, 
  Phone, 
  MapPin, 
  Award, 
  Play,
  Activity,
  Stethoscope,
  Shield,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Clock,
  Users,
  Zap,
  HeartPulse,
  Siren,
  CircleDot,
  Eye,
  Scan,
  Target,
  ArrowRight,
  Check,
  AlertCircle,
  Info,
  Sparkles,
  Gauge,
  Waves,
  CircleCheck,
  Loader2
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

// Animation variants - slower, more controlled
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.12
    }
  }
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#expertise", label: "Expertise" },
    { href: "#precision", label: "Precision" },
    { href: "#cases", label: "Outcomes" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'glass-nav py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container flex items-center justify-between">
        <a href="#" className="flex items-center gap-2.5" data-testid="logo-link">
          <Heart className="w-6 h-6 text-[#2E6F6D]" strokeWidth={1.5} />
          <span className="font-['Playfair_Display'] text-xl font-semibold text-[#1F2A37]">
            Dr. Aravind
          </span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-[#4B5563] hover:text-[#1F2A37] transition-colors duration-300 text-sm font-medium"
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:block">
          <a href="#contact">
            <Button 
              className="btn-primary text-sm"
              data-testid="nav-consultation-btn"
            >
              Book Consultation
            </Button>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-[#1F2A37]" />
          ) : (
            <Menu className="w-6 h-6 text-[#1F2A37]" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden glass-nav"
          >
            <div className="container py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-[#4B5563] hover:text-[#1F2A37] transition-colors py-2 text-lg"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
              <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="btn-primary w-full mt-4" data-testid="mobile-consultation-btn">
                  Book Consultation
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section - refined
const HeroSection = () => {
  return (
    <section className="min-h-screen flex items-center pt-24 pb-16 md:pt-28" data-testid="hero-section">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="order-2 lg:order-1"
          >
            <motion.div variants={fadeInUp} className="mb-5">
              <span className="section-badge">
                <Award className="w-3.5 h-3.5" strokeWidth={1.5} />
                Interventional Cardiology
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.1] mb-6 text-[#1F2A37]"
            >
              Precision heart care for{" "}
              <span className="text-[#2E6F6D]">complex conditions</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-base text-[#4B5563] mb-8 max-w-lg leading-relaxed"
            >
              Dr. Aravind Duruvasal specializes in advanced interventional procedures, 
              image-guided precision, and managing high-risk cardiac cases with care 
              and confidence.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-3 mb-10">
              <a href="#contact">
                <Button className="btn-primary flex items-center gap-2" data-testid="hero-consultation-btn">
                  Schedule Consultation
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </a>
              <a href="#expertise">
                <Button variant="outline" className="btn-secondary" data-testid="hero-expertise-btn">
                  View Expertise
                </Button>
              </a>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-6">
              <div className="stat-card px-5 py-3">
                <p className="text-2xl font-['Playfair_Display'] font-semibold text-[#1F2A37]">15+</p>
                <p className="text-xs text-[#9CA3AF]">Years Experience</p>
              </div>
              <div className="stat-card px-5 py-3">
                <p className="text-2xl font-['Playfair_Display'] font-semibold text-[#1F2A37]">10K+</p>
                <p className="text-xs text-[#9CA3AF]">Procedures</p>
              </div>
              <div className="stat-card px-5 py-3">
                <p className="text-2xl font-['Playfair_Display'] font-semibold text-[#1F2A37]">98%</p>
                <p className="text-xs text-[#9CA3AF]">Success Rate</p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="relative">
              <div className="hero-image-mask rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_heart-trusted/artifacts/pwp4wpcv_Aravind%20Portrait.jpeg"
                  alt="Dr. Aravind Duruvasal"
                  className="w-full h-auto object-cover"
                />
              </div>
              
              {/* Floating credential card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="absolute -bottom-5 -left-5 md:left-auto md:-right-5 bg-white rounded-xl p-4 shadow-lg border border-[#E5E7EB]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#2E6F6D]/10 flex items-center justify-center">
                    <Stethoscope className="w-5 h-5 text-[#2E6F6D]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1F2A37] text-sm">MBBS, MD, DM</p>
                    <p className="text-xs text-[#9CA3AF]">Interventional Cardiology</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Clinical Expertise Section
const ExpertiseSection = () => {
  const expertiseAreas = [
    {
      category: "Coronary Interventions",
      icon: Target,
      items: [
        { name: "Multi-vessel Disease", desc: "Complete revascularization without bypass" },
        { name: "Left Main Stenting", desc: "Complex left main disease treatment" },
        { name: "Chronic Total Occlusion (CTO)", desc: "Opening completely blocked arteries" },
        { name: "ACS & STEMI", desc: "Emergency heart attack intervention" }
      ]
    },
    {
      category: "Structural Heart",
      icon: Heart,
      items: [
        { name: "TAVI / TAVR", desc: "Valve replacement without surgery" },
        { name: "Mitral Clip Therapy", desc: "Minimally invasive valve repair" },
        { name: "Tricuspid Interventions", desc: "Right-sided valve treatments" },
        { name: "PFO/ASD Closure", desc: "Closing heart defects" }
      ]
    },
    {
      category: "Heart Rhythm & Devices",
      icon: Activity,
      items: [
        { name: "Pacemaker Implantation", desc: "Restoring normal heart rhythm" },
        { name: "ICD Implantation", desc: "Protection from dangerous rhythms" },
        { name: "CRT-D Devices", desc: "Improving heart pumping efficiency" },
        { name: "Lead Extraction", desc: "Safe removal of old leads" }
      ]
    },
    {
      category: "Preventive & Vascular",
      icon: Shield,
      items: [
        { name: "Renal Denervation", desc: "Treating resistant hypertension" },
        { name: "Advanced Lipid Therapy", desc: "Evolocumab, Inclisiran protocols" },
        { name: "Familial Hypercholesterolemia", desc: "Genetic cholesterol management" },
        { name: "EVAR", desc: "Aortic aneurysm repair" }
      ]
    }
  ];

  return (
    <section id="expertise" className="section bg-white" data-testid="expertise-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-14"
        >
          <span className="section-badge mb-4 inline-flex">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
            Clinical Expertise
          </span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl leading-tight mb-4 text-[#1F2A37]">
            Comprehensive cardiac care
          </h2>
          <p className="text-[#4B5563]">
            From routine interventions to complex structural procedures, 
            expertise across the full spectrum of modern cardiology.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5">
          {expertiseAreas.map((area, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="feature-card card-hover h-full" data-testid={`expertise-card-${index}`}>
                <CardContent className="p-0">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg bg-[#2E6F6D]/10 flex items-center justify-center">
                      <area.icon className="w-5 h-5 text-[#2E6F6D]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-lg text-[#1F2A37]">
                      {area.category}
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {area.items.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 group">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#8FAEA4] mt-2 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-[#1F2A37]">{item.name}</p>
                          <p className="text-xs text-[#9CA3AF]">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Precision Imaging Section
const PrecisionImagingSection = () => {
  const [activeView, setActiveView] = useState('precision');

  const imagingTechniques = [
    {
      name: "IVUS",
      fullName: "Intravascular Ultrasound",
      description: "Sound waves create detailed images from inside the artery, revealing plaque structure and vessel size."
    },
    {
      name: "OCT",
      fullName: "Optical Coherence Tomography",
      description: "Light-based imaging provides ultra-high resolution views of artery walls and stent placement."
    },
    {
      name: "NIRS",
      fullName: "Near-Infrared Spectroscopy",
      description: "Identifies lipid-rich plaques at risk of rupture, enabling preventive intervention."
    }
  ];

  return (
    <section id="precision" className="section" data-testid="precision-section">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-badge mb-4 inline-flex">
              <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
              Precision Imaging
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl leading-tight mb-4 text-[#1F2A37]">
              We see inside the artery
            </h2>
            <p className="text-[#4B5563] mb-8">
              No guesswork. Advanced intravascular imaging reveals what standard angiograms 
              cannot — the true nature of blockages and optimal treatment approach.
            </p>

            {/* Imaging Toggle */}
            <div className="imaging-toggle mb-8">
              <button
                className={activeView === 'angio' ? 'active' : ''}
                onClick={() => setActiveView('angio')}
                data-testid="toggle-angio"
              >
                Standard Angiogram
              </button>
              <button
                className={activeView === 'precision' ? 'active' : ''}
                onClick={() => setActiveView('precision')}
                data-testid="toggle-precision"
              >
                Precision Imaging
              </button>
            </div>

            <AnimatePresence mode="wait">
              {activeView === 'angio' ? (
                <motion.div
                  key="angio"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="guidance-panel"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="font-medium text-[#1F2A37] mb-1">Limited view</p>
                      <p className="text-sm text-[#4B5563]">
                        Traditional angiograms show only the silhouette of blockages. 
                        Plaque composition, vessel wall health, and optimal stent sizing 
                        remain hidden.
                      </p>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="precision"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3"
                >
                  {imagingTechniques.map((tech, i) => (
                    <div key={i} className="guidance-panel hover-lift">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#2E6F6D]/10 flex items-center justify-center flex-shrink-0">
                          <Scan className="w-4 h-4 text-[#2E6F6D]" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="font-medium text-[#1F2A37] text-sm">
                            {tech.name} <span className="font-normal text-[#9CA3AF]">— {tech.fullName}</span>
                          </p>
                          <p className="text-xs text-[#4B5563] mt-1">{tech.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#1F2A37]">
              {/* Video container with teal tint overlay */}
              <div className="relative aspect-square">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="w-full h-full object-cover"
                  data-testid="precision-video"
                  poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'%3E%3Crect fill='%231F2A37' width='400' height='400'/%3E%3Ccircle cx='200' cy='200' r='80' fill='none' stroke='%232E6F6D' stroke-width='2'/%3E%3Ccircle cx='200' cy='200' r='60' fill='none' stroke='%238FAEA4' stroke-width='1.5'/%3E%3Cpath d='M120 200 Q150 160 200 180 Q250 200 280 180' fill='none' stroke='%232E6F6D' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_heart-trusted/artifacts/9s68w7vs_Medical%20-%20Motion%20Graphics%20%28Adaptation%29.mp4" 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>
                
                {/* Teal tint overlay */}
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(46, 111, 109, 0.12) 0%, rgba(143, 174, 164, 0.08) 100%)',
                    mixBlendMode: 'multiply'
                  }}
                />
              </div>
              
              {activeView === 'precision' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-md border border-[#E5E7EB]"
                >
                  <p className="text-xs font-medium text-[#2E6F6D]">Sub-millimeter precision</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// MCS / High-Risk Procedures Section
const HighRiskSection = () => {
  const mcsDevices = [
    {
      name: "Impella Support",
      description: "Temporary heart pump that maintains blood flow during complex procedures, allowing safe treatment of critical cases.",
      icon: HeartPulse
    },
    {
      name: "IABP (Balloon Pump)",
      description: "Assists the heart by inflating and deflating in sync with heartbeat, reducing workload during recovery.",
      icon: Waves
    },
    {
      name: "Protected PCI",
      description: "High-risk stenting with circulatory support for patients who would otherwise face limited options.",
      icon: Shield
    }
  ];

  return (
    <section className="section bg-white" data-testid="highrisk-section">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="order-2 lg:order-1"
          >
            <div className="mcs-visual">
              <div className="flex items-center justify-center py-8">
                <div className="relative">
                  {/* Heart with support visualization */}
                  <motion.div
                    animate={{ scale: [1, 1.03, 1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Heart className="w-32 h-32 text-[#8FAEA4]" strokeWidth={1} fill="rgba(143, 174, 164, 0.2)" />
                  </motion.div>
                  
                  {/* Support indicator */}
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -right-6 top-1/2 -translate-y-1/2"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#2E6F6D] flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                  </motion.div>
                </div>
              </div>
              <p className="text-center text-sm text-[#8FAEA4] mt-4">
                Mechanical support enables safe intervention
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <span className="section-badge mb-4 inline-flex">
              <Gauge className="w-3.5 h-3.5" strokeWidth={1.5} />
              High-Risk Procedures
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl leading-tight mb-4 text-[#1F2A37]">
              Safely managing complexity
            </h2>
            <p className="text-[#4B5563] mb-8">
              Some patients require advanced support during intervention. 
              Mechanical circulatory support allows us to safely treat cases 
              that would otherwise carry unacceptable risk.
            </p>

            <div className="space-y-4">
              {mcsDevices.map((device, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="guidance-panel hover-lift"
                  data-testid={`mcs-device-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#8FAEA4]/15 flex items-center justify-center flex-shrink-0">
                      <device.icon className="w-5 h-5 text-[#2E6F6D]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium text-[#1F2A37] mb-1">{device.name}</p>
                      <p className="text-sm text-[#4B5563]">{device.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Symptom Guidance Tool
const SymptomGuidanceSection = () => {
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [showGuidance, setShowGuidance] = useState(false);

  const symptoms = [
    { id: 'chest', label: 'Chest pressure or discomfort', icon: Heart },
    { id: 'breath', label: 'Shortness of breath', icon: Siren },
    { id: 'palpitations', label: 'Palpitations or racing heart', icon: Activity },
    { id: 'swelling', label: 'Leg or ankle swelling', icon: CircleDot },
    { id: 'dizziness', label: 'Dizziness or lightheadedness', icon: Loader2 },
    { id: 'hypertension', label: 'Resistant high blood pressure', icon: Gauge },
    { id: 'family', label: 'Family history of heart disease', icon: Users },
    { id: 'preventive', label: 'Preventive check (no symptoms)', icon: Shield }
  ];

  const toggleSymptom = (id) => {
    setSelectedSymptoms(prev => 
      prev.includes(id) 
        ? prev.filter(s => s !== id) 
        : [...prev, id]
    );
    setShowGuidance(false);
  };

  const getGuidance = () => {
    if (selectedSymptoms.length === 0) return null;
    
    const hasUrgent = selectedSymptoms.some(s => ['chest', 'breath', 'dizziness'].includes(s));
    const hasModerate = selectedSymptoms.some(s => ['palpitations', 'swelling', 'hypertension'].includes(s));
    const isPreventive = selectedSymptoms.includes('preventive') && selectedSymptoms.length === 1;

    if (hasUrgent) {
      return {
        urgency: 'high',
        title: 'Consider prompt evaluation',
        message: 'These symptoms can have cardiac causes that benefit from timely assessment. If symptoms are severe or sudden, seek immediate care.',
        action: 'Schedule priority consultation'
      };
    } else if (hasModerate) {
      return {
        urgency: 'moderate',
        title: 'Evaluation recommended',
        message: 'These symptoms warrant a thorough cardiac evaluation to identify any underlying conditions and determine the best approach.',
        action: 'Book consultation'
      };
    } else if (isPreventive) {
      return {
        urgency: 'low',
        title: 'Preventive assessment',
        message: 'Proactive heart health evaluation can identify risk factors early. Consider comprehensive screening with advanced lipid analysis and imaging if appropriate.',
        action: 'Discuss prevention options'
      };
    } else {
      return {
        urgency: 'moderate',
        title: 'Personalized evaluation',
        message: 'Based on your selection, a detailed consultation can help determine your cardiac health status and appropriate next steps.',
        action: 'Schedule consultation'
      };
    }
  };

  const guidance = showGuidance ? getGuidance() : null;

  return (
    <section className="section" data-testid="symptom-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <span className="section-badge mb-4 inline-flex">
            <Info className="w-3.5 h-3.5" strokeWidth={1.5} />
            Symptom Guide
          </span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl leading-tight mb-4 text-[#1F2A37]">
            Not sure what your symptoms mean?
          </h2>
          <p className="text-[#4B5563]">
            Select what you're experiencing for general guidance. 
            This is not a diagnosis — only a consultation can provide that.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center mb-8">
            {symptoms.map((symptom, i) => (
              <motion.button
                key={symptom.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                onClick={() => toggleSymptom(symptom.id)}
                className={`symptom-chip ${selectedSymptoms.includes(symptom.id) ? 'selected' : ''}`}
                data-testid={`symptom-${symptom.id}`}
              >
                <symptom.icon className="w-4 h-4" strokeWidth={1.5} />
                {symptom.label}
              </motion.button>
            ))}
          </div>

          {selectedSymptoms.length > 0 && !showGuidance && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <Button 
                onClick={() => setShowGuidance(true)}
                className="btn-primary"
                data-testid="get-guidance-btn"
              >
                Get Guidance
              </Button>
            </motion.div>
          )}

          <AnimatePresence>
            {guidance && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="mt-8"
              >
                <div className={`guidance-panel border-l-4 ${
                  guidance.urgency === 'high' ? 'border-l-[#DC2626]' : 
                  guidance.urgency === 'moderate' ? 'border-l-[#D97706]' : 
                  'border-l-[#2E6F6D]'
                }`} data-testid="guidance-panel">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      guidance.urgency === 'high' ? 'bg-red-50' : 
                      guidance.urgency === 'moderate' ? 'bg-amber-50' : 
                      'bg-[#2E6F6D]/10'
                    }`}>
                      {guidance.urgency === 'high' ? (
                        <AlertCircle className="w-5 h-5 text-[#DC2626]" strokeWidth={1.5} />
                      ) : guidance.urgency === 'moderate' ? (
                        <Info className="w-5 h-5 text-[#D97706]" strokeWidth={1.5} />
                      ) : (
                        <CircleCheck className="w-5 h-5 text-[#2E6F6D]" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[#1F2A37] mb-2">{guidance.title}</p>
                      <p className="text-sm text-[#4B5563] mb-4">{guidance.message}</p>
                      <a href="#contact">
                        <Button className="btn-primary text-sm" data-testid="guidance-cta">
                          {guidance.action}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-[#9CA3AF] text-center mt-4">
                  This guidance is informational only and does not constitute medical advice.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

// Case Stories Section
const CaseStoriesSection = () => {
  const [expandedCase, setExpandedCase] = useState(null);

  const cases = [
    {
      id: 1,
      headline: "Left Main Blockage — Surgery Avoided",
      age: "67",
      issue: "Critical left main artery disease",
      approach: "IVUS-guided left main stenting with DES",
      outcome: "Complete revascularization, normal activity resumed",
      narrative: "Patient presented with severe angina and was initially recommended bypass surgery. Advanced imaging revealed favorable anatomy for percutaneous intervention. Using IVUS guidance, precise stent sizing and optimal placement achieved excellent results with rapid recovery.",
      imaging: true
    },
    {
      id: 2,
      headline: "Chronic Total Occlusion Opened",
      age: "58",
      issue: "Complete blockage of right coronary artery for 8 months",
      approach: "Antegrade CTO recanalization with OCT guidance",
      outcome: "Restored blood flow, symptoms resolved",
      narrative: "Long-standing complete occlusion causing persistent angina despite medication. Successful wire crossing and stenting restored blood flow. Patient returned to exercise without symptoms.",
      imaging: true
    },
    {
      id: 3,
      headline: "High-Risk TAVI Success",
      age: "82",
      issue: "Severe aortic stenosis, declined for surgery",
      approach: "Transfemoral TAVI under MCS support",
      outcome: "Valve function normalized, independent living restored",
      narrative: "Elderly patient with multiple comorbidities and severe aortic stenosis was not a surgical candidate. Transcatheter valve replacement performed with mechanical support as backup. Discharged within 3 days.",
      imaging: false
    },
    {
      id: 4,
      headline: "Complex Multi-vessel Disease",
      age: "54",
      issue: "Triple vessel disease with reduced heart function",
      approach: "Staged protected PCI with Impella support",
      outcome: "All vessels treated, heart function improved",
      narrative: "Diabetic patient with severe three-vessel disease and EF of 30%. Staged approach with Impella support during high-risk left main and LAD intervention. Complete revascularization achieved with improvement in heart function.",
      imaging: true
    }
  ];

  return (
    <section id="cases" className="section bg-white" data-testid="cases-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-14"
        >
          <span className="section-badge mb-4 inline-flex">
            <CircleCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
            Outcomes
          </span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl leading-tight mb-4 text-[#1F2A37]">
            Real patients. Real outcomes.
          </h2>
          <p className="text-[#4B5563]">
            Complex conditions handled with precision and care. 
            Each case represents a patient who found clarity and confidence.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5">
          {cases.map((caseItem, index) => (
            <motion.div
              key={caseItem.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div 
                className="case-card cursor-pointer"
                onClick={() => setExpandedCase(expandedCase === caseItem.id ? null : caseItem.id)}
                data-testid={`case-card-${index}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-['Playfair_Display'] text-lg text-[#1F2A37] mb-1">
                        {caseItem.headline}
                      </h3>
                      <p className="text-xs text-[#9CA3AF]">Age {caseItem.age}</p>
                    </div>
                    <motion.div
                      animate={{ rotate: expandedCase === caseItem.id ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-5 h-5 text-[#9CA3AF]" strokeWidth={1.5} />
                    </motion.div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-medium text-[#9CA3AF] w-16 flex-shrink-0">Issue:</span>
                      <span className="text-sm text-[#4B5563]">{caseItem.issue}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-medium text-[#9CA3AF] w-16 flex-shrink-0">Approach:</span>
                      <span className="text-sm text-[#4B5563]">{caseItem.approach}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-medium text-[#9CA3AF] w-16 flex-shrink-0">Outcome:</span>
                      <span className="text-sm text-[#2E6F6D] font-medium">{caseItem.outcome}</span>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedCase === caseItem.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-[#E5E7EB] pt-4 mt-4"
                      >
                        <p className="text-sm text-[#4B5563] mb-4">{caseItem.narrative}</p>
                        {caseItem.imaging && (
                          <div className="flex items-center gap-2 text-xs text-[#2E6F6D]">
                            <Scan className="w-4 h-4" strokeWidth={1.5} />
                            <span>Procedure guided by intravascular imaging</span>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Awards Section - subtle
const AwardsSection = () => {
  const awards = [
    { title: "Young Cardiologist of the Year", org: "ET Achievers" },
    { title: "CSI Nick Shining Star", org: "Cardiological Society of India" },
    { title: "Gold Medal — Best Outgoing Student", org: "Academic Excellence" }
  ];

  return (
    <section className="py-12" data-testid="awards-section">
      <div className="container">
        <div className="flex flex-wrap items-center justify-center gap-6">
          {awards.map((award, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex items-center gap-3 px-4 py-2"
              data-testid={`award-${i}`}
            >
              <Award className="w-4 h-4 text-[#8FAEA4]" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-[#1F2A37]">{award.title}</p>
                <p className="text-xs text-[#9CA3AF]">{award.org}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const locations = [
    {
      name: "Prashant Hospital",
      area: "Velacherry, Chennai",
      phone: "+91 9500 231 167"
    },
    {
      name: "Armoraa Heart Care",
      area: "Alwarpet, Chennai",
      phone: "+91 9500 231 104"
    }
  ];

  return (
    <section id="contact" className="section bg-[#1F2A37]" data-testid="contact-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-xl mx-auto mb-12"
        >
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl leading-tight mb-4 text-white">
            Ready to take the first step?
          </h2>
          <p className="text-[#9CA3AF]">
            Your heart health journey begins with a conversation.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {locations.map((location, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="bg-white/5 rounded-xl p-5 border border-white/10 hover:border-[#8FAEA4]/30 transition-colors duration-400" data-testid={`location-card-${index}`}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#2E6F6D]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#8FAEA4]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{location.name}</h3>
                    <p className="text-sm text-[#9CA3AF]">{location.area}</p>
                  </div>
                </div>
                
                <a 
                  href={`tel:${location.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors duration-300 group"
                  data-testid={`call-btn-${index}`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#2E6F6D] flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                    <Phone className="w-4 h-4 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs text-[#9CA3AF]">Call to schedule</p>
                    <p className="text-white font-medium">{location.phone}</p>
                  </div>
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-sm text-[#9CA3AF] mt-10"
        >
          Monday – Saturday, 9 AM – 6 PM
        </motion.p>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-6 bg-[#181F26]" data-testid="footer">
      <div className="container">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-[#8FAEA4]" strokeWidth={1.5} />
            <span className="font-['Playfair_Display'] text-white text-sm">
              Dr. Aravind Duruvasal
            </span>
          </div>
          <p className="text-xs text-[#9CA3AF]">
            MBBS, MD, DM — Interventional Cardiology
          </p>
          <p className="text-xs text-[#4B5563]">
            © {new Date().getFullYear()} All rights reserved
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-[#F8F9F7]">
      <Navbar />
      <main>
        <HeroSection />
        <div className="divider" />
        <ExpertiseSection />
        <PrecisionImagingSection />
        <HighRiskSection />
        <SymptomGuidanceSection />
        <CaseStoriesSection />
        <div className="divider" />
        <AwardsSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
